import { Box, Grid } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { ArrowBack as BackArrowIcon } from '@tassat/icon';
import React from 'react';

import TassatLogoAndName from 'Images/TassatLogoAndName.svg';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

interface IActionOverlayProps {
    className?: string;
    admin: boolean;
    title: string;
    type?: string;
    headerDetailsComponent?: React.ReactElement;
    invertHeaderColors?: boolean;
    goBack: (event?: any) => void;
    children: React.ReactNode;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => {
    const isGlobalHeaderEnabled = (props: IActionOverlayProps) => (props.admin && theme?.app?.clientTheme.layoutSettings.useGlobalHeader) ||
        (!props.admin && theme?.app?.clientTheme.layoutSettings.useClientGlobalHeader);

    return {
        root: {
            position: (props: IActionOverlayProps) => (!isGlobalHeaderEnabled(props as IActionOverlayProps) ?
                'fixed' as const
                : 'absolute' as const),
            width: (props: IActionOverlayProps) => (!isGlobalHeaderEnabled(props as IActionOverlayProps) ?
                'calc(100% - 100px)'
                : '100%'),
            maxHeight: '100vh',
            height: '100%',
            top: 0,
            right: 0
        },
        gradient: {
            width: '24px',
            boxShadow: 'inset -0.8rem 0px 2rem -1rem black'
        },
        headerText: {
            padding: '15px 0px 15px 40px',
            fontSize: '30px',
            alignSelf: 'center' as const,
            textTransform: (props: IActionOverlayProps) => (!props.invertHeaderColors ?
                'uppercase' as const
                : null)
        },
        backArrow: {
            textDecoration: 'none' as const,
            height: '24px',
            width: '40px',
            cursor: 'pointer' as const
        },
        overlayBody: {
            padding: '40px',
            color: '#333',
            backgroundColor: theme?.app?.applicationColors.enumColors.offWhite ||
                '#F5F5F5',
            overflowY: 'auto' as const
        },
        headerContainer: {
            flexBasis: 'auto' as const,
            marginRight: '5rem'
        },
        tassatpayLogoContainer: {
            position: 'absolute' as const,
            top: 'calc(50% - 0.56rem)',
            left: 'calc(50% - 7.5rem)'
        },
        tassatpayLogo: {
            height: '1.5rem',
            width: '7.5rem'
        },
        detailsContainer: {
            maxWidth: 'auto' as const,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        overlayHeader: {
            maxHeight: '120px',
            position: 'relative' as const,
            flexBasis: 'auto' as const,
            padding: '.25rem 0',
            color: (props: IActionOverlayProps) => {
                if (props.invertHeaderColors) {
                    return theme?.app?.clientTheme.colors.primaryColor ||
                        '#375073';
                }

                return theme?.app?.applicationColors.enumColors.white ||
                    '#fff';
            },
            backgroundColor: (props: IActionOverlayProps) => {
                if (props.invertHeaderColors) {
                    return theme?.app?.applicationColors.enumColors.white ||
                        '#fff';
                }

                return theme?.app?.clientTheme.colors.overlays?.headerColor ||
                    theme?.app?.clientTheme.colors.primaryColorLight ||
                    '#009BDF';
            }
        },
        '@media (max-width: 1200px)': {
            headerContainer: { marginRight: '2.5rem' },
            detailsContainer: { maxWidth: '320px' }
        }
    };
});

export function ActionOverlayComponent(props: IActionOverlayProps) {
    const {
        admin,
        title,
        headerDetailsComponent,
        goBack,
        children,
        className
    } = props;

    const classes = muiStyles(props);
    const theme = useTheme<ApplicationMuiTheme>();
    const {
        useAppLogoOnOverlay,
        useGlobalHeader,
        useClientGlobalHeader
    } = theme?.app?.clientTheme.layoutSettings || {};

    const isGlobalHeaderEnabled = React.useMemo(() => {
        if (admin && useGlobalHeader) return true;
        if (!admin && useClientGlobalHeader) return true;

        return false;
    }, [ admin, useGlobalHeader, useClientGlobalHeader ]);

    return (
        <Grid
            className={`${classes.root} ${className}`}
            container
            direction='row'
            spacing={0}
            wrap='nowrap'
        >
            {!isGlobalHeaderEnabled && <div className={classes.gradient} />}
            <Grid
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.overlayHeader}
                    container
                    direction='row'
                    item
                    justifyContent='flex-start'
                    sm={12}
                    spacing={0}
                >
                    <Grid
                        className={classes.headerContainer}
                        item
                    >
                        <Box className={classes.headerText}>
                            {goBack &&
                                <span onClick={goBack} >
                                    <BackArrowIcon
                                        className={classes.backArrow}
                                    />
                                </span>}
                            {title}
                        </Box>
                    </Grid>
                    {headerDetailsComponent &&
                        (
                        <Grid
                            className={classes.detailsContainer}
                            item
                        >
                            {headerDetailsComponent}
                        </Grid>
                    )}
                    {useAppLogoOnOverlay &&
                        (
                        <Grid
                            className={classes.tassatpayLogoContainer}
                            item
                        >
                            <img
                                className={classes.tassatpayLogo}
                                src={TassatLogoAndName}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    className={classes.overlayBody}
                    container
                    direction='row'
                    item
                    spacing={0}
                    xs={12}
                >
                    {children}
                </Grid>
            </Grid>
        </Grid>
    );
}

export { ActionOverlayComponent as ActionOverlay };